import React from 'react'
import { useSelector } from 'react-redux'
import ReactSelect from '@base/react-select-global'
import { IValueEditorProps } from '@app-types/general'
import { createLogger } from '@utils/logger'
import Link from '@components/Link'
import useValueEditor from '@hooks/useValueEditor'
import { useViewContext } from '@base/ViewContext'
import { selectFieldSelectOptionsByFieldId } from '@reducers/data/field_select_option'

const FieldOptionsManager = React.lazy(() => import('@features/field_options_manager/FieldOptionsManager'))

// Initialize logger
const log = createLogger('MultiSelectEditor', { prependScope: true })
log.setLevel('info')

// Define interfaces for props and related data structures

type MultiSelectEditorProps = Omit<IValueEditorProps, 'serializeValue' | 'parseValue'> & {
  value: number[] | null
  onSave?: (value: number[] | null) => void
  onDone?: () => void
  serializeValue?: (value: number[] | null) => number[] | null
  parseValue?: (value: number[] | null) => number[] | null
  tableId: string | number
  fieldId?: string
}

const passthrough = (value: number[] | null): number[] | null => (value ? value : null)
const defaultParseValueFn = passthrough
const defaultSerializeValueFn = passthrough

// Convert between react-select option objects and number[] (IDs)
type OptionType = { value: number; label: string }
const toSelectOptions = (options: any[]): OptionType[] => options.map(o => ({ value: o.id, label: o.label }))
const toValueOptions = (ids: number[] | null | undefined, options: OptionType[]): OptionType[] => {
  if (!Array.isArray(ids)) return []
  return options.filter(opt => ids.includes(opt.value))
}
const fromValueOptions = (selected: OptionType[] | null): number[] | null => {
  if (!selected || selected.length === 0) return null
  return selected.map(opt => opt.value)
}
type ViewContextValueType = {
  tableId?: string
}

const MultiSelectEditor: React.FC<MultiSelectEditorProps> & { EDITOR_TYPE: string } = (props) => {  
  const {
    name,
    value = [],
    onSave,
    onDone,
    onChange,
    onEditCancel,
    parseValue = defaultParseValueFn,
    serializeValue = defaultSerializeValueFn,
    tableId: propTableId,
    fieldId,
  } = props
  // Get some props from context, if not passed as props
  const context = useViewContext<ViewContextValueType>()
  const tableId = propTableId ?? context.tableId

  // Warn if onDone and onSave are both passed
  // When we pass onSave handler, we expect this component to decide when it should call onSave (to save the value)
  // When we pass onDone handler, we expect the parent component to decide when it's appropriate to save the value
  if (onDone && onSave) {
    log.warn('onDone and onSave are both passed as props. This is not recommended.')
  }
  if(!fieldId) {
    log.warn("FieldId prop is missing for MultiSelectEditor. This is required to load field options.")
  }

  const rawOptions = fieldId ? useSelector((state: any) => selectFieldSelectOptionsByFieldId(state, fieldId)) : []
  const options = rawOptions.map(opt => ({ id: Number(opt.id), label: opt.attributes.label }))

  const { localValue, handleLocalChange } = useValueEditor<number[] | null, number[] | null>({
    value,
    parseValue,
    serializeValue,
    onChange: onChange ? ({ value }) => onChange({ name, value }) : undefined,
    onSave,
    onEditCancel,
  })

  const handleSelectChange = (opts: readonly OptionType[] | null) => {
    const ids = fromValueOptions(Array.isArray(opts) ? opts : [])
    handleLocalChange(ids)
  }

  const getOptionManagerLinkContent = () => {
    if(fieldId && tableId) {
      return (
        <div className="multi-select-editor__option-manager-link">
          <React.Suspense fallback={<span className="muted">Loading…</span>}>
            <FieldOptionsManager tableId={tableId} fieldId={fieldId} autoOpen={false} onLoadingChange={setOptionsLoading} trigger={open => (
              <Link onClick={e => { e.preventDefault(); open() }} href="#">
                Manage field options
              </Link>
            )} />
          </React.Suspense>
        </div>
      )
    } else {
      log.warn("TableId and FieldId props are both missing for MultiSelectEditor. TableId is required to load field options.")
    }
    return null
  }
  // Track loading state of options (provided by FieldOptionsManager)
  const [isOptionsLoading, setOptionsLoading] = React.useState(false)

  const selectOptions = toSelectOptions(options)
  const selectedOptions = toValueOptions(localValue, selectOptions)

  log.debug('Rendering MultiSelectEditor', { name, value, localValue, selectedOptions, selectOptions, options, isOptionsLoading })

  return (
    <span className="content-editor content-editor--multi-select multi-select-editor">
      <div className="multi-select-editor__option-selection">
        <ReactSelect
          isMulti
          name={name}
          options={selectOptions}
          value={selectedOptions}
          isLoading={isOptionsLoading}
          onChange={handleSelectChange}
          placeholder="Select options..."
        />
      </div>
      {getOptionManagerLinkContent()}
    </span>
  )
}

MultiSelectEditor.EDITOR_TYPE = 'multi-select'

export default MultiSelectEditor
